import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { marked } from 'marked'
import { useAuth } from '../context/AuthContext'
import type { DirectMessage, PublicUser, MessageReaction } from '../types'
import { useCallContext } from '../context/CallContext'
import { usePresence } from '../hooks/usePresence'
import { useMultiSelect } from '../hooks/useMultiSelect'
import { attachmentIsImage, attachmentIsAudio, attachmentIcon, copyToClipboard } from '../utils/attachments'
import { ArrowLeftIcon, UserIcon, LoaderIcon, ImageIcon, FileIcon, XIcon, ReplyIcon, PhoneIcon, VideoIcon, AudioIcon, BellIcon, BellOffIcon, BotIcon, SearchIcon, CheckIcon, DownloadIcon, EditIcon, ChevronDownIcon, CopyIcon, TrashIcon, MegaphoneIcon } from '../icons'
import { PresenceDot } from './PresenceDot'
import { WhatsAppAudioPlayer } from './WhatsAppAudioPlayer'
import { EmojiPicker, QUICK_REACTIONS } from './EmojiPicker'
import { ChatInputBar } from './ChatInputBar'
import { CallView } from './CallView'
import { avatarUrl } from '../utils/avatar'
import { useNotificationContext } from '../context/NotificationContext'
import { linkifyText } from '../utils/linkify'
import { API_URL } from '../api'
import { getToken } from '../api'


export function DMView({ userId: propUserId }: { userId?: string }) {
  const routeParams = useParams()
  const userId = propUserId || routeParams.userId
  const navigate = useNavigate()
  const { user, token } = useAuth()
  const call = useCallContext()
  const presence = usePresence()
  const [messages, setMessages] = useState<DirectMessage[]>([])
  const [otherUser, setOtherUser] = useState<PublicUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [replyTo, setReplyTo] = useState<DirectMessage | null>(null)
  const [editingMsg, setEditingMsg] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [msgOptionsMenu, setMsgOptionsMenu] = useState<{ msgId: string; x: number; y: number; canCopy: boolean; canEdit: boolean; canDelete: boolean } | null>(null) // kept for compatibility
  const [contextMenu, setContextMenu] = useState<{ msgId: string; x: number; y: number; canCopy: boolean; canEdit: boolean; canDelete: boolean } | null>(null)
  const multiSelect = useMultiSelect()
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [atlasBot, setAtlasBot] = useState<PublicUser | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<DirectMessage[] | null>(null) // null = not searching, [] = searched
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; name: string; type: string } | null>(null)
  const [reactionPickerFor, setReactionPickerFor] = useState<{ msgId: string; x: number; y: number; full?: boolean } | null>(null)
  const [activeTab, setActiveTab] = useState('chat') // chat | media
  const { mutedUsers, muteUser, unmuteUser, fetchUnreadCount } = useNotificationContext()
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadingMoreRef = useRef(false)
  const hasMoreRef = useRef(true)

  const scrollToMessage = useCallback((msgId: string) => {
    const container = scrollContainerRef.current
    if (!container) return
    const el = container.querySelector(`[data-msg-id="${msgId}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.add('msg-highlight')
    setTimeout(() => el.classList.remove('msg-highlight'), 2000)
  }, [])

  const authHeaders = useMemo(() => (token ? { 'Authorization': `Bearer ${token}` } : {}) as Record<string, string>, [token])

  // Clear multi-select when switching conversations
  useEffect(() => { multiSelect.clear() }, [userId])

  const loadMessages = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch(`${API_URL}/api/dm/${userId}`, { headers: authHeaders })
      const data = await res.json()
      const serverMessages = data.messages || []
      setMessages(prev => {
        // Preserve optimistic messages (sent but not yet confirmed by server).
        // Optimistic messages have a temporary UUID id that won't match any
        // server message. We detect them by checking if their id exists in
        // the server response. If not, they're still pending — keep them.
        if (!prev || prev.length === 0) return serverMessages
        const serverIds = new Set(serverMessages.map((m: DirectMessage) => m.id))
        const pending = prev.filter(m => !serverIds.has(m.id))
        // Merge: server messages + pending optimistic messages (sorted by time)
        const merged = [...serverMessages, ...pending]
        merged.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        return merged
      })
      setHasMore((data.messages || []).length >= 20)
      hasMoreRef.current = (data.messages || []).length >= 20
    } catch {
      // Don't wipe messages on network error — keep what we have
    } finally {
      setLoading(false)
    }
  }, [userId, authHeaders])

  const loadOlderMessages = useCallback(async () => {
    if (!userId || loadingMoreRef.current || !hasMoreRef.current) return
    if (messages.length === 0) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    const container = scrollContainerRef.current
    const prevScrollHeight = container?.scrollHeight || 0
    const prevScrollTop = container?.scrollTop || 0
    try {
      const oldest = messages[0]
      const before = oldest?.created_at
      const res = await fetch(`${API_URL}/api/dm/${userId}?before=${encodeURIComponent(before)}`, { headers: authHeaders })
      const data = await res.json()
      const older = data.messages || []
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev])
        // Preserve scroll position: adjust scrollTop by the height added
        // Use double rAF to ensure DOM has fully rendered the new messages
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight
              container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight)
            }
          })
        })
      }
      if (older.length < 20) {
        setHasMore(false)
        hasMoreRef.current = false
      }
    } catch {
      // ignore
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [userId, authHeaders, messages])

  const loadOtherUser = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: authHeaders })
      const data = await res.json()
      const found = (data.users || []).find((u: PublicUser) => String(u.id) === String(userId))
      if (found) setOtherUser(found)
      const bot = (data.users || []).find((u: PublicUser) => u.email === 'atlas@colossus7.com')
      if (bot) setAtlasBot(bot)
    } catch {
      // ignore
    }
  }, [userId, authHeaders])

  // Clear messages and state when switching to a different user
  useEffect(() => {
    setMessages([])
    setLoading(true)
    setOtherUser(null)
    setReplyTo(null)
    setEditingMsg(null)
    setSearchQuery('')
  }, [userId])

  useEffect(() => {
    loadMessages()
    loadOtherUser()
    // Mark messages from this user as read
    if (userId) {
      fetch(`${API_URL}/api/dm/read/${userId}`, { method: 'POST', headers: authHeaders })
        .then(res => {
          if (res.ok) fetchUnreadCount?.()
        })
        .catch(() => {})
    }
  }, [loadMessages, loadOtherUser, userId, authHeaders, fetchUnreadCount])

  // Reactions are now embedded in messages (m.reactions) and synced via WebSocket.
  // Listen for real-time reaction updates.
  useEffect(() => {
    const handleReactionUpdate = (e: Event) => {
      const { message_type, message_id, reactions: newReactions } = (e as CustomEvent).detail || {}
      if (message_type !== 'dm') return
      setMessages(prev => prev.map(m =>
        m.id === message_id ? { ...m, reactions: newReactions || [] } : m
      ))
    }
    window.addEventListener('reaction-update', handleReactionUpdate)
    return () => window.removeEventListener('reaction-update', handleReactionUpdate)
  }, [])

  // Listen for real-time message edits
  useEffect(() => {
    const handleDmEdited = (e: Event) => {
      const msg = (e as CustomEvent).detail?.message
      if (!msg) return
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, content: msg.content } : m))
    }
    window.addEventListener('dm_edited', handleDmEdited)
    return () => window.removeEventListener('dm_edited', handleDmEdited)
  }, [])

  // Listen for real-time message deletions
  useEffect(() => {
    const handleDmDeleted = (e: Event) => {
      const msgId = (e as CustomEvent).detail?.message_id
      if (!msgId) return
      setMessages(prev => prev.filter(m => m.id !== msgId))
    }
    window.addEventListener('dm_deleted', handleDmDeleted)
    return () => window.removeEventListener('dm_deleted', handleDmDeleted)
  }, [])

  // Listen for real-time bulk message deletions
  useEffect(() => {
    const handleDmDeletedBulk = (e: Event) => {
      const ids = (e as CustomEvent).detail?.message_ids
      if (!ids || !Array.isArray(ids)) return
      const idSet = new Set(ids)
      setMessages(prev => prev.filter(m => !idSet.has(m.id)))
    }
    window.addEventListener('dm_deleted_bulk', handleDmDeletedBulk)
    return () => window.removeEventListener('dm_deleted_bulk', handleDmDeletedBulk)
  }, [])

  const toggleReaction = async (msgId: string, emoji: string) => {
    const msg = messages.find(m => m.id === msgId)
    const msgReactions = msg?.reactions || []
    const existing = msgReactions.find(r => r.emoji === emoji && r.user_id === user?.id)
    try {
      if (existing) {
        const res = await fetch(`${API_URL}/api/reactions`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ message_type: 'dm', message_id: msgId, emoji }),
        })
        if (res.ok) {
          const data = await res.json()
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, reactions: data.reactions || [] } : m
          ))
        }
      } else {
        const res = await fetch(`${API_URL}/api/reactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ message_type: 'dm', message_id: msgId, emoji }),
        })
        if (res.ok) {
          const data = await res.json()
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, reactions: data.reactions || [] } : m
          ))
        }
      }
    } catch { /* ignore */ }
    setReactionPickerFor(null)
  }

  // Close context menu on click anywhere
  useEffect(() => {
    const closeMenu = () => setContextMenu(null)
    if (contextMenu) {
      window.addEventListener('click', closeMenu)
      window.addEventListener('scroll', closeMenu, true)
      return () => {
        window.removeEventListener('click', closeMenu)
        window.removeEventListener('scroll', closeMenu, true)
      }
    }
  }, [contextMenu])

  // Close message options menu on click outside
  useEffect(() => {
    const closeOptions = () => setMsgOptionsMenu(null)
    if (msgOptionsMenu) {
      window.addEventListener('click', closeOptions)
      window.addEventListener('scroll', closeOptions, true)
      return () => {
        window.removeEventListener('click', closeOptions)
        window.removeEventListener('scroll', closeOptions, true)
      }
    }
  }, [msgOptionsMenu])

  // Listen for real-time DM notifications
  useEffect(() => {
    const handleNotification = (e: Event) => {
      const notif = (e as CustomEvent).detail
      // Reload if the message is from the user we're viewing, or from Atlas bot (8)
      if (String(notif.sender_id) === String(userId)) {
        loadMessages()
        // Re-mark as read on backend since new messages arrived while viewing
        fetch(`${API_URL}/api/dm/read/${userId}`, { method: 'POST', headers: authHeaders })
          .then(res => {
            if (res.ok) fetchUnreadCount?.()
          })
          .catch(() => {})
      }
    }
    window.addEventListener('dm-notification', handleNotification)
    return () => window.removeEventListener('dm-notification', handleNotification)
  }, [userId, loadMessages, authHeaders, fetchUnreadCount])

  // Reload messages when the tab regains visibility/focus — catches up
  // any messages missed while the notification socket was silently dead.
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') loadMessages()
    }
    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('focus', handleVisible)
    return () => {
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('focus', handleVisible)
    }
  }, [loadMessages])

  const firstLoadRef = useRef(true)
  useEffect(() => {
    if (loading || messages.length === 0) return
    // Skip auto-scroll when prepending older messages (scroll position is preserved separately)
    if (loadingMoreRef.current) return
    if (firstLoadRef.current) {
      // Instant scroll to bottom on first load — no animation
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
      })
      firstLoadRef.current = false
    } else {
      // Smooth scroll for new messages
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, loading])

  // Reset first-load flag when switching conversations
  useEffect(() => {
    firstLoadRef.current = true
    setHasMore(true)
    hasMoreRef.current = true
    setActiveTab('chat')
  }, [userId])

  // Trigger transcription for audio messages that don't have one yet
  useEffect(() => {
    const pendingAudioMsgs = messages.filter(m =>
      m.attachment_url && m.attachment_url !== 'uploading' &&
      attachmentIsAudio(m.attachment_type) && !m.transcription_text
    )
    if (pendingAudioMsgs.length === 0) return
    // Trigger transcription for each pending audio (POST triggers it on demand)
    pendingAudioMsgs.forEach(m => {
      fetch(`${API_URL}/api/dm/transcribe/${m.id}`, { method: 'POST', headers: authHeaders })
        .then(r => r.json())
        .then(data => {
          if (data.transcription) {
            setMessages(prev => prev.map(msg => msg.id === m.id ? { ...msg, transcription_text: data.transcription } : msg))
          }
        })
        .catch(() => {})
    })
  }, [messages, authHeaders])

  const handleEditMessage = async (msgId: string, newContent: string) => {
    try {
      const res = await fetch(`${API_URL}/api/dm/edit/${msgId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newContent }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to edit')
      }
      const updated = await res.json()
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: updated.content } : m))
      setEditingMsg(null)
      setEditText('')
    } catch (err: unknown) {
      console.error('Edit failed:', err)
      setEditingMsg(null)
      setEditText('')
    }
  }

  const startEditing = (msg: DirectMessage) => {
    setEditingMsg(msg.id)
    setEditText(msg.content || '')
  }

  const cancelEditing = () => {
    setEditingMsg(null)
    setEditText('')
  }

  const handleSend = async (text: string, files: File[], currentReplyTo: unknown) => {
    const replyToMsg = currentReplyTo as DirectMessage | null
    setSending(true)
    const fileArr = Array.isArray(files) ? files : (files ? [files] : [])

    // If no files, send single text message
    if (fileArr.length === 0) {
      const optimistic = {
        id: crypto.randomUUID(),
        sender_id: user!.id,
        recipient_id: userId,
        content: text,
        attachment_url: '',
        attachment_type: '',
        attachment_name: '',
        reply_to_id: replyToMsg?.id || null,
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, optimistic])
      try {
        const res = await fetch(`${API_URL}/api/dm/${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ content: text, reply_to_id: replyToMsg?.id || undefined }),
        })
        const data = await res.json()
        if (res.ok) {
          setMessages(prev => prev.map(m => m.id === optimistic.id ? data : m))
        } else {
          setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, content: `${data.error || 'Falha'}` } : m))
        }
      } catch {
        setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, content: 'Erro de conexão' } : m))
      } finally {
        setSending(false)
        setReplyTo(null)
      }
      return
    }

    // Send text first (if any), then each file as separate message
    if (text) {
      const textOptimistic = {
        id: crypto.randomUUID(),
        sender_id: user!.id,
        recipient_id: userId,
        content: text,
        attachment_url: '',
        attachment_type: '',
        attachment_name: '',
        reply_to_id: replyToMsg?.id || null,
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, textOptimistic])
      try {
        const res = await fetch(`${API_URL}/api/dm/${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ content: text, reply_to_id: replyToMsg?.id || undefined }),
        })
        const data = await res.json()
        if (res.ok) {
          setMessages(prev => prev.map(m => m.id === textOptimistic.id ? data : m))
        }
      } catch { /* ignore */ }
    }

    // Send each file as a separate message
    for (const file of fileArr) {
      const optimistic = {
        id: crypto.randomUUID(),
        sender_id: user!.id,
        recipient_id: userId,
        content: '',
        attachment_url: 'uploading',
        attachment_type: file?.type || '',
        attachment_name: file?.name || '',
        reply_to_id: replyToMsg?.id || null,
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, optimistic])
      try {
        const formData = new FormData()
        formData.append('content', '')
        formData.append('attachment', file)
        if (replyToMsg) formData.append('reply_to_id', String(replyToMsg.id))
        const res = await fetch(`${API_URL}/api/dm/${userId}`, {
          method: 'POST',
          headers: { ...authHeaders },
          body: formData,
        })
        const data = await res.json()
        if (res.ok) {
          setMessages(prev => prev.map(m => m.id === optimistic.id ? data : m))
        } else {
          setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, content: `${data.error || 'Falha'}`, attachment_url: '' } : m))
        }
      } catch {
        setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...m, content: 'Erro de conexão', attachment_url: '' } : m))
      }
    }
    setSending(false)
    setReplyTo(null)
  }

  const handleSendAudio = async (file: File) => {
    setSending(true)

    const optimistic = {
      id: crypto.randomUUID(),
      sender_id: user!.id,
      recipient_id: userId,
      content: '',
      attachment_url: 'uploading',
      attachment_type: 'audio/webm',
      attachment_name: file.name,
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimistic])

    try {
      const formData = new FormData()
      formData.append('content', '')
      formData.append('attachment', file)
      const res = await fetch(`${API_URL}/api/dm/${userId}`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData,
      })
      const data = await res.json()
      if (res.ok) {
        setMessages(prev => prev.map(m => m.id === optimistic.id ? data : m))
      }
    } catch {
      // ignore
    } finally {
      setSending(false)
    }
  }

  const formatTime = (ts: string | number | Date | undefined) => {
    if (!ts) return ''
    const d = new Date(ts)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    if (isToday) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const findReplyMessage = (replyToId: string | null | undefined) => {
    if (!replyToId) return null
    return messages.find(m => m.id === replyToId) || replyCache[replyToId] || null
  }

  // Cache for reply messages fetched from server (not in loaded history)
  const replyCacheRef = useRef<Record<string, DirectMessage | null>>({})
  const [replyCache, setReplyCache] = useState<Record<string, DirectMessage | null>>({})

  const findReplyMessageAsync = useCallback(async (replyToId: string | null | undefined) => {
    if (!replyToId) return null
    // Check local messages first
    const local = messages.find(m => m.id === replyToId)
    if (local) return local
    // Check cache
    if (replyCacheRef.current[replyToId]) return replyCacheRef.current[replyToId]
    // Fetch from server
    try {
      const res = await fetch(`${API_URL}/api/dm/${userId}?msg_id=${encodeURIComponent(replyToId)}`, { headers: authHeaders })
      const data = await res.json()
      const msg = data.message
      if (msg) {
        replyCacheRef.current[replyToId] = msg
        setReplyCache(prev => ({ ...prev, [replyToId]: msg }))
        return msg
      }
    } catch { /* ignore */ }
    return null
  }, [messages, userId, authHeaders])

  // Fetch reply messages for visible messages that have reply_to_id but no local reply
  useEffect(() => {
    messages.forEach(m => {
      if (m.reply_to_id && !findReplyMessage(m.reply_to_id) && !replyCacheRef.current[m.reply_to_id]) {
        replyCacheRef.current[m.reply_to_id] = null // mark as pending to avoid duplicate fetches
        findReplyMessageAsync(m.reply_to_id)
      }
    })
  }, [messages, findReplyMessageAsync])

  // Debounced server-side search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/dm/${userId}?q=${encodeURIComponent(searchQuery)}`, { headers: authHeaders })
        const data = await res.json()
        if (!cancelled) setSearchResults(data.messages || [])
      } catch {
        if (!cancelled) setSearchResults([])
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [searchQuery, userId, authHeaders])

  const displayMessages = searchQuery ? (searchResults || []) : messages

  // Group consecutive image-only messages from the same sender into albums (WhatsApp-style).
  // Only messages with an uploaded image attachment, no text, and within a 60s window are grouped.
  const renderGroups = useMemo(() => {
    type MessageGroup =
      | { type: 'album'; senderId: string; lastTime: Date; messages: DirectMessage[] }
      | { type: 'single'; message: DirectMessage }
    const groups: MessageGroup[] = []
    let currentAlbum: { senderId: string; lastTime: Date; messages: DirectMessage[] } | null = null
    const ALBUM_WINDOW_MS = 60_000

    const isGroupableImage = (m: DirectMessage) =>
      m.attachment_url &&
      m.attachment_url !== 'uploading' &&
      attachmentIsImage(m.attachment_type) &&
      (!m.content || !m.content.trim()) &&
      !(m.message_type && m.message_type.startsWith('call'))

    for (const m of displayMessages) {
      if (isGroupableImage(m)) {
        if (currentAlbum &&
            currentAlbum.senderId === m.sender_id &&
            (new Date(m.created_at).getTime() - currentAlbum.lastTime.getTime()) < ALBUM_WINDOW_MS) {
          currentAlbum.messages.push(m)
          currentAlbum.lastTime = new Date(m.created_at)
        } else {
          if (currentAlbum) groups.push({ type: 'album', ...currentAlbum })
          currentAlbum = { senderId: m.sender_id, lastTime: new Date(m.created_at), messages: [m] }
        }
      } else {
        if (currentAlbum) { groups.push({ type: 'album', ...currentAlbum }); currentAlbum = null }
        groups.push({ type: 'single', message: m })
      }
    }
    if (currentAlbum) groups.push({ type: 'album', ...currentAlbum })
    // Single-image albums render as regular single messages
    return groups.map(g => g.type === 'album' && g.messages.length === 1
      ? { type: 'single' as const, message: g.messages[0] } : g)
  }, [displayMessages])

  // Media files from messages
  const mediaFiles = messages.filter(m => m.attachment_url && m.attachment_url !== 'uploading')
  const attUrl = (m: DirectMessage) => {
    const url = m.attachment_url
    if (!url || url === 'uploading') return ''
    // CDN URL → use directly
    if (url.startsWith('https://cdn.colossus7.com/')) return url
    // /api/media/ → convert to CDN
    if (url.startsWith('/api/media/')) return 'https://cdn.colossus7.com/' + url.slice('/api/media/'.length)
    // Other absolute URLs → use directly
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    // Fallback: authenticated proxy
    return `${API_URL}/api/attachment/${m.id}?token=${encodeURIComponent(getToken() || '')}`
  }

  const downloadAttachment = async (url: string, name: string) => {
    // Use anchor with download attribute — works for same-origin URLs
    // and triggers download instead of navigation
    const a = document.createElement('a')
    a.href = url
    a.download = name || 'arquivo'
    a.target = '_blank'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
  const imageFiles = mediaFiles.filter(m => attachmentIsImage(m.attachment_type))
  const videoFiles = mediaFiles.filter(m => m.attachment_type && m.attachment_type.startsWith('video/'))
  const audioFiles = mediaFiles.filter(m => attachmentIsAudio(m.attachment_type))
  const docFiles = mediaFiles.filter(m =>
    !attachmentIsImage(m.attachment_type) &&
    !attachmentIsAudio(m.attachment_type) &&
    !(m.attachment_type && m.attachment_type.startsWith('video/'))
  )

  return (
    <div className="hermes-chat-page">
      <div className="hermes-chat-header">
        <button className="icon-btn" onClick={() => navigate('/messages')}>
          <ArrowLeftIcon size={18} />
        </button>
        <div className="hermes-chat-title">
          <div
            className="hermes-chat-msg-avatar presence-avatar"
            style={{ width: 28, height: 28, cursor: 'pointer' }}
            onClick={() => otherUser && setShowProfileModal(true)}
            onContextMenu={(e) => {
              e.preventDefault()
              if (otherUser) setShowProfileModal(true)
            }}
            title="Clique ou botão direito para ver o perfil"
          >
            {otherUser?.avatar_url ? (
              <img src={avatarUrl(otherUser)} alt="" className="avatar-img" />
            ) : (
              <UserIcon size={16} />
            )}
            <PresenceDot status={presence.getUserPresence(userId!).status} size={9} />
          </div>
          <div
            className="hermes-chat-title-text"
            style={{ cursor: 'pointer' }}
            onClick={() => otherUser && setShowProfileModal(true)}
            onContextMenu={(e) => {
              e.preventDefault()
              if (otherUser) setShowProfileModal(true)
            }}
            title="Clique ou botão direito para ver o perfil"
          >
            <span>
              {otherUser?.display_name || otherUser?.name || otherUser?.email || `Usuário #${userId}`}
              {mutedUsers?.has(userId ?? '') && <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', color: 'var(--text-muted)' }}><BellOffIcon size={12} /></span>}
            </span>
            <span className="presence-text" style={{ color: presence.getUserPresence(userId!).status === 'online' ? '#2ea043' : presence.getUserPresence(userId!).status === 'away' ? '#d29922' : presence.getUserPresence(userId!).status === 'busy' ? '#f85149' : '#6e7681' }}>
              {presence.getUserPresence(userId!).status === 'online' ? 'Online' : presence.getUserPresence(userId!).status === 'away' ? 'Ausente' : presence.getUserPresence(userId!).status === 'busy' ? 'Ocupado' : 'Offline'}
            </span>
          </div>
        </div>
        <div className="dm-header-actions">
          <button
            className="icon-btn"
            onClick={() => otherUser && setShowProfileModal(true)}
            title="Ver perfil"
          >
            <UserIcon size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => { setSearchOpen(!searchOpen); if (searchOpen) setSearchQuery('') }}
            title="Buscar mensagens"
          >
            <SearchIcon size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => call.startCall(userId!, false)}
            title="Chamada de voz"
          >
            <PhoneIcon size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => call.startCall(userId!, true)}
            title="Chamada de vídeo"
          >
            <VideoIcon size={18} />
          </button>
        </div>
        {searchOpen && (
          <div className="dm-search-bar">
            <SearchIcon size={16} />
            <input
              type="text"
              placeholder="Buscar nesta conversa..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <span className="dm-search-count">
                {searchResults === null ? 'Buscando...' : `${searchResults.length} ${searchResults.length === 1 ? 'resultado' : 'resultados'}`}
              </span>
            )}
            <button className="icon-btn" onClick={() => { setSearchOpen(false); setSearchQuery('') }}>
              <XIcon size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="dm-tabs">
        <button
          className={`dm-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          Conversa
        </button>
        <button
          className={`dm-tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Mídia {mediaFiles.length > 0 && <span className="dm-tab-badge">{mediaFiles.length}</span>}
        </button>
      </div>

      {multiSelect.isSelecting && (
        <div className="bulk-action-bar">
          <span className="bulk-action-count">
            {multiSelect.selected.size} selecionada{multiSelect.selected.size !== 1 ? 's' : ''}
          </span>
          <button
            className="bulk-action-btn bulk-action-danger"
            onClick={async () => {
              const ids = [...multiSelect.selected]
              if (!confirm(`Apagar ${ids.length} mensagem(ns)?`)) return
              try {
                await fetch(`${API_URL}/api/dm/delete-bulk`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...authHeaders },
                  body: JSON.stringify({ message_ids: ids }),
                })
                setMessages(prev => prev.filter(m => !ids.includes(m.id)))
                multiSelect.clear()
              } catch (err: unknown) {
                console.error('Bulk delete failed:', err)
              }
            }}
          >
            <TrashIcon size={16} /> Apagar
          </button>
          <button className="bulk-action-btn" onClick={() => multiSelect.clear()}>
            <XIcon size={16} /> Cancelar
          </button>
        </div>
      )}

      {activeTab === 'chat' ? (
      <>
      {/* Embedded 1:1 call — only for active/outgoing calls with this user.
          Incoming calls (ringing) still use the global overlay so they show
          even when the user is on a different page. */}
      {!call.isChannelCall && !call.incomingCall && (call.callState !== 'idle') && call.callTargetId === userId && (
        <CallView call={call} onClose={() => {}} embedded />
      )}
      <div
        className="hermes-chat-messages"
        ref={scrollContainerRef}
        onScroll={(e) => {
          const el = e.currentTarget
          if (el.scrollTop < 50 && !searchQuery) loadOlderMessages()
        }}
      >
        {loading ? (
          <div className="hermes-loading">Carregando mensagens...</div>
        ) : displayMessages.length === 0 ? (
          <div className="hermes-chat-welcome">
            <SearchIcon size={48} />
            <h2>{searchQuery ? 'Nenhum resultado' : 'Nenhuma mensagem encontrada'}</h2>
            <p>{searchQuery ? 'Tente buscar por outro termo.' : 'Comece a conversar.'}</p>
          </div>
        ) : (
          <>
          {loadingMore && !searchQuery && (
            <div className="hermes-loading-more" style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: 13 }}>
              Carregando mensagens antigas...
            </div>
          )}
          {renderGroups.map(group => {
            // Album: consecutive image-only messages grouped into one bubble (WhatsApp-style)
            if (group.type === 'album') {
              const album = group
              const firstMsg = album.messages[0]
              const lastMsg = album.messages[album.messages.length - 1]
              const isMine = firstMsg.sender_id === user?.id
              const isAtlas = firstMsg.sender_id === atlasBot?.id
              const colCount = Math.min(album.messages.length === 1 ? 1 : album.messages.length <= 4 ? 2 : 3, 3)

              return (
                <div
                  key={`album-${firstMsg.id}`}
                  data-msg-id={firstMsg.id}
                  className={`hermes-chat-msg ${isMine ? 'user' : 'assistant'}${isAtlas ? ' atlas-bot-msg' : ''}${multiSelect.isSelecting ? ' msg-selectable' : ''}`}
                  onClick={(e) => {
                    if (e.shiftKey || multiSelect.isSelecting) {
                      e.preventDefault()
                      const idx = displayMessages.findIndex(x => x.id === firstMsg.id)
                      multiSelect.toggleSelect(firstMsg.id, idx)
                    }
                  }}
                >
                  {multiSelect.isSelecting && (
                    <div className="msg-select-checkbox" onClick={(e) => {
                      e.stopPropagation()
                      const idx = displayMessages.findIndex(x => x.id === firstMsg.id)
                      multiSelect.toggleSelect(firstMsg.id, idx)
                    }}>
                      <div className={`msg-checkbox-box ${multiSelect.selected.has(firstMsg.id) ? 'checked' : ''}`}>
                        {multiSelect.selected.has(firstMsg.id) && <CheckIcon size={14} />}
                      </div>
                    </div>
                  )}
                  <div className="hermes-chat-msg-avatar">
                    {isMine ? (
                      user?.avatar_url ? <img src={avatarUrl(user)} alt="" className="avatar-img" /> : <UserIcon size={18} />
                    ) : isAtlas ? (
                      atlasBot?.avatar_url ? <img src={avatarUrl(atlasBot)} alt="" className="avatar-img" /> : <BotIcon size={18} />
                    ) : (
                      otherUser?.avatar_url ? <img src={avatarUrl(otherUser)} alt="" className="avatar-img" /> : <UserIcon size={18} />
                    )}
                  </div>
                  <div className="hermes-chat-msg-body">
                    <div className="hermes-chat-msg-header">
                      <span className="hermes-chat-msg-role">
                        {isMine ? 'Você' : isAtlas ? (atlasBot?.display_name || 'Atlas') : (otherUser?.display_name || otherUser?.name || otherUser?.email || 'Usuário')}
                      </span>
                      <span className="hermes-chat-msg-time">{formatTime(lastMsg.created_at)}</span>
                    </div>
                    <div className="dm-msg-bubble">
                      <div className={`dm-album-grid dm-album-cols-${colCount}`}>
                        {album.messages.map((am: DirectMessage, i: number) => (
                          <div
                            key={am.id}
                            data-msg-id={am.id}
                            className={`dm-album-item${i === album.messages.length - 1 ? ' dm-album-item-last' : ''}`}
                            onClick={() => setPreviewAttachment({ url: attUrl(am), name: am.attachment_name, type: am.attachment_type })}
                          >
                            <img src={attUrl(am)} alt={am.attachment_name || ''} loading="lazy" />
                            {i === album.messages.length - 1 && (
                              <span className="dm-album-time">{formatTime(lastMsg.created_at)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Download all button */}
                      <button
                        className="dm-album-download-all"
                        onClick={(e) => {
                          e.stopPropagation()
                          album.messages.forEach((am: DirectMessage) => {
                            const a = document.createElement('a')
                            a.href = attUrl(am)
                            a.download = am.attachment_name || `imagem-${am.id}.jpg`
                            a.target = '_blank'
                            a.rel = 'noopener'
                            document.body.appendChild(a)
                            a.click()
                            document.body.removeChild(a)
                          })
                        }}
                        title={`Baixar ${album.messages.length} fotos`}
                      >
                        <DownloadIcon size={14} />
                      </button>
                      {/* Reaction button */}
                      <button
                        className="message-reaction-btn"
                        onClick={(e) => { e.stopPropagation(); setReactionPickerFor({ msgId: firstMsg.id, x: e.clientX, y: e.clientY }) }}
                        title="Reagir"
                      >
                        <span style={{ fontSize: 14 }}>😊</span>
                      </button>
                      {/* Reply button */}
                      <button
                        className="dm-reply-btn"
                        onClick={() => setReplyTo(lastMsg)}
                        title="Responder"
                      >
                        <ReplyIcon size={14} />
                      </button>
                      {/* Options button */}
                      <button
                        className="msg-options-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          const rect = e.currentTarget.getBoundingClientRect()
                          setMsgOptionsMenu({ msgId: firstMsg.id, x: rect.right, y: rect.bottom + 4, canCopy: false, canEdit: false, canDelete: isMine })
                        }}
                        title="Opções"
                      >
                        <ChevronDownIcon size={16} />
                      </button>
                      {/* Reactions on first message */}
                      {firstMsg.reactions && firstMsg.reactions.length > 0 && (
                        <div className="message-reactions">
                          {Object.entries(
                            firstMsg.reactions.reduce((acc: Record<string, MessageReaction[]>, r: MessageReaction) => {
                              if (!acc[r.emoji]) acc[r.emoji] = []
                              acc[r.emoji].push(r)
                              return acc
                            }, {} as Record<string, MessageReaction[]>)
                          ).map(([emoji, users]: [string, MessageReaction[]]) => {
                            const names = users.map((u: MessageReaction) => {
                              if (u.user_id === user?.id) return 'Você'
                              return otherUser?.display_name || otherUser?.name || 'Usuário'
                            })
                            return (
                              <button
                                key={emoji}
                                className={`message-reaction ${users.some((u: MessageReaction) => u.user_id === user?.id) ? 'mine' : ''}`}
                                onClick={() => toggleReaction(firstMsg.id, emoji)}
                                title={names.join(', ')}
                              >
                                <span>{emoji}</span>
                                <span className="message-reaction-count">{users.length}</span>
                                <span className="message-reaction-names">{names.join(', ')}</span>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            }

            const m = group.message
            const isMine = m.sender_id === user?.id
            const isAtlas = m.sender_id === atlasBot?.id
            const repliedMsg = findReplyMessage(m.reply_to_id)

            // Call invite messages — show a button to join the call
            if (m.message_type === 'call_invite') {
              let inviteData: { inviter_name?: string; call_id?: string; channel_id?: string; is_video?: boolean } | null = null
              try { inviteData = JSON.parse(m.content) } catch {}
              const inviterName = inviteData?.inviter_name || 'Alguém'
              const callId = inviteData?.call_id
              const chId = inviteData?.channel_id || null
              const isVideo = !!inviteData?.is_video
              const callActive = call.callState === 'connected' || call.callState === 'calling'
              return (
                <div key={m.id} className="dm-call-invite">
                  <div className="dm-call-invite-icon">
                    {isVideo ? <VideoIcon size={20} /> : <PhoneIcon size={20} />}
                  </div>
                  <div className="dm-call-invite-body">
                    <span className="dm-call-invite-text">
                      {inviterName} está te convidando para uma chamada{isVideo ? ' de vídeo' : ' de áudio'}
                    </span>
                    <button
                      className="dm-call-invite-btn"
                      onClick={() => {
                        if (!callId) return
                        if (callActive) return
                        call.joinChannelCall(callId, isVideo, chId, false, null, false, chId ? null : (userId || null))
                      }}
                      disabled={callActive}
                    >
                      {callActive ? 'Em chamada' : 'Entrar na chamada'}
                    </button>
                  </div>
                  <span className="dm-call-time">{formatTime(m.created_at)}</span>
                </div>
              )
            }

            // Call event messages
            if (m.message_type && m.message_type.startsWith('call')) {
              const isVideo = m.message_type.includes('video')
              const isEnd = m.message_type.includes('end')
              const duration = m.call_duration || 0
              const durStr = duration > 0
                ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`
                : ''
              return (
                <div key={m.id} className="dm-call-event">
                  <div className="dm-call-icon">
                    {isVideo ? <VideoIcon size={12} /> : <PhoneIcon size={12} />}
                  </div>
                  <span>
                    {isEnd ? 'Chamada finalizada' : 'Chamada iniciada'}
                    {isVideo ? ' (vídeo)' : ' (áudio)'}
                    {durStr && ` · ${durStr}`}
                  </span>
                  <span className="dm-call-time">{formatTime(m.created_at)}</span>
                </div>
              )
            }

            return (
              <div
                key={m.id}
                data-msg-id={m.id}
                className={`hermes-chat-msg ${isMine ? 'user' : 'assistant'}${isAtlas ? ' atlas-bot-msg' : ''}${multiSelect.selected.has(m.id) ? ' msg-selected' : ''}${multiSelect.isSelecting ? ' msg-selectable' : ''}`}
                onDoubleClick={() => multiSelect.isSelecting ? null : setReplyTo(m)}
                onClick={(e) => {
                  if (e.shiftKey || multiSelect.isSelecting) {
                    e.preventDefault()
                    const idx = displayMessages.findIndex(x => x.id === m.id)
                    if (e.shiftKey && multiSelect.lastSelectedIdx.current !== null) {
                      const ids = displayMessages.map(x => x.id)
                      multiSelect.selectRange(ids, multiSelect.lastSelectedIdx.current, idx)
                    } else {
                      multiSelect.toggleSelect(m.id, idx)
                    }
                  }
                }}
              >
                {multiSelect.isSelecting && (
                  <div className="msg-select-checkbox" onClick={(e) => {
                    e.stopPropagation()
                    const idx = displayMessages.findIndex(x => x.id === m.id)
                    multiSelect.toggleSelect(m.id, idx)
                  }}>
                    <div className={`msg-checkbox-box ${multiSelect.selected.has(m.id) ? 'checked' : ''}`}>
                      {multiSelect.selected.has(m.id) && <CheckIcon size={14} />}
                    </div>
                  </div>
                )}
                <div className="hermes-chat-msg-avatar">
                  {isMine ? (
                    user?.avatar_url ? <img src={avatarUrl(user)} alt="" className="avatar-img" /> : <UserIcon size={18} />
                  ) : isAtlas ? (
                    atlasBot?.avatar_url ? <img src={avatarUrl(atlasBot)} alt="" className="avatar-img" /> : <BotIcon size={18} />
                  ) : (
                    otherUser?.avatar_url ? <img src={avatarUrl(otherUser)} alt="" className="avatar-img" /> : <UserIcon size={18} />
                  )}
                </div>
                <div className="hermes-chat-msg-body">
                  <div className="hermes-chat-msg-header">
                    <span className="hermes-chat-msg-role">
                      {isMine ? 'Você' : isAtlas ? (atlasBot?.display_name || 'Atlas') : (otherUser?.display_name || otherUser?.name || otherUser?.email || 'Usuário')}
                    </span>
                    {m.message_type === 'broadcast' && (
                      <span className="dm-broadcast-badge" title="Mensagem enviada para todos">
                        <MegaphoneIcon size={11} />
                        Mensagem para todos
                      </span>
                    )}
                    <span className="hermes-chat-msg-time">{formatTime(m.created_at)}</span>
                  </div>
                  {repliedMsg && (
                    <div className="dm-reply-quote" onClick={() => scrollToMessage(repliedMsg.id)}>
                      {repliedMsg.attachment_url && repliedMsg.attachment_url !== 'uploading' && attachmentIsImage(repliedMsg.attachment_type) ? (
                        <img
                          src={`${API_URL}/api/attachment/${repliedMsg.id}?token=${encodeURIComponent(getToken() || '')}`}
                          alt={repliedMsg.attachment_name}
                          className="dm-reply-img"
                        />
                      ) : (
                        <span className="dm-reply-text">
                          {repliedMsg.content || (repliedMsg.attachment_name ? `[${repliedMsg.attachment_name}]` : '...')}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="dm-msg-bubble">
                  {m.attachment_url && m.attachment_url !== 'uploading' && (
                    <div className={`dm-attachment ${isMine ? 'dm-attachment-mine' : 'dm-attachment-other'}`}>
                      {attachmentIsImage(m.attachment_type) ? (
                        <img
                          src={attUrl(m)}
                          alt={m.attachment_name}
                          className="dm-attachment-img"
                          onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                        />
                      ) : attachmentIsAudio(m.attachment_type) ? (
                        <div className="dm-audio-attachment">
                          <WhatsAppAudioPlayer
                            src={attUrl(m)}
                            fileName={m.attachment_name || 'audio'}
                            isOutgoing={m.sender_id === user?.id}
                          />
                          {m.transcription_text && (
                            <div className="dm-audio-transcription">
                              <span className="dm-audio-transcription-label">Transcrição:</span>
                              <span className="dm-audio-transcription-text">{m.transcription_text}</span>
                            </div>
                          )}
                          {!m.transcription_text && m.attachment_url !== 'uploading' && (
                            <div className="dm-audio-transcription dm-audio-transcription-pending">
                              <span className="dm-audio-transcription-label">Transcrevendo áudio...</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          className="dm-attachment-file"
                          onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                        >
                          {attachmentIcon(m.attachment_type)}
                          <span>{m.attachment_name || 'Arquivo'}</span>
                        </button>
                      )}
                    </div>
                  )}
                  {m.attachment_url === 'uploading' && (
                    <div className="dm-attachment dm-attachment-uploading">
                      <LoaderIcon size={16} /> Enviando anexo...
                    </div>
                  )}
                  {editingMsg === m.id ? (
                    <div className="msg-edit-inline">
                      <textarea
                        className="msg-edit-textarea"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditMessage(m.id, editText) }
                          if (e.key === 'Escape') cancelEditing()
                        }}
                        autoFocus
                        rows={1}
                      />
                      <div className="msg-edit-actions">
                        <button className="msg-edit-save" onClick={() => handleEditMessage(m.id, editText)}><CheckIcon size={14} /> Salvar</button>
                        <button className="msg-edit-cancel" onClick={cancelEditing}>Cancelar</button>
                      </div>
                    </div>
                  ) : m.content && (isAtlas ? (
                    <div
                      className="hermes-chat-msg-content markdown-body"
                      dangerouslySetInnerHTML={{ __html: marked.parse(m.content || '', { breaks: true }) }}
                    />
                  ) : (
                    <div className="hermes-chat-msg-content">{linkifyText(m.content)}</div>
                  ))}
                  {/* Reaction button on bubble hover */}
                  {reactionPickerFor?.msgId === m.id && !reactionPickerFor?.full ? (
                    <div className={`reaction-quick-wrapper ${isMine ? '' : 'reaction-left'}`}>
                      <EmojiPicker
                        quickOnly
                        x={reactionPickerFor!.x}
                        y={reactionPickerFor!.y}
                        onSelect={(emoji) => {
                          if (emoji === 'open-picker') {
                            setReactionPickerFor({ msgId: m.id, full: true, x: reactionPickerFor!.x, y: reactionPickerFor!.y })
                          } else {
                            toggleReaction(m.id, emoji)
                          }
                        }}
                        onClose={() => setReactionPickerFor(null)}
                      />
                    </div>
                  ) : reactionPickerFor?.msgId === m.id && reactionPickerFor?.full ? (
                    <div className={`reaction-quick-wrapper ${isMine ? '' : 'reaction-left'}`}>
                      <EmojiPicker
                        x={reactionPickerFor!.x}
                        y={reactionPickerFor!.y}
                        onSelect={(emoji) => toggleReaction(m.id, emoji)}
                        onClose={() => setReactionPickerFor(null)}
                      />
                    </div>
                  ) : (
                    <button
                      className="message-reaction-btn"
                      onClick={(e) => { e.stopPropagation(); setReactionPickerFor({ msgId: m.id, x: e.clientX, y: e.clientY }) }}
                      title="Reagir"
                    >
                      <span style={{ fontSize: 14 }}>😊</span>
                    </button>
                  )}
                {/* Reply button — inside bubble for correct positioning */}
                <button
                  className="dm-reply-btn"
                  onClick={() => setReplyTo(m)}
                  title="Responder"
                >
                  <ReplyIcon size={14} />
                </button>
                {/* Options button — shows on hover, opens menu with Copy + Edit + Delete */}
                {(m.content || m.attachment_url) && editingMsg !== m.id && (
                  <button
                    className="msg-options-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      const rect = e.currentTarget.getBoundingClientRect()
                      setMsgOptionsMenu({ msgId: m.id, x: rect.right, y: rect.bottom + 4, canCopy: !!m.content, canEdit: isMine && m.message_type === 'text', canDelete: isMine })
                    }}
                    title="Opções"
                  >
                    <ChevronDownIcon size={16} />
                  </button>
                )}
                  </div>
                {/* Reactions — inside bubble for WhatsApp-style corner positioning */}
                {m.reactions && m.reactions.length > 0 && (
                  <div className="message-reactions">
                    {Object.entries(
                      m.reactions.reduce((acc: Record<string, MessageReaction[]>, r: MessageReaction) => {
                        if (!acc[r.emoji]) acc[r.emoji] = []
                        acc[r.emoji].push(r)
                        return acc
                      }, {} as Record<string, MessageReaction[]>)
                    ).map(([emoji, users]: [string, MessageReaction[]]) => {
                      const names = users.map((u: MessageReaction) => {
                        if (u.user_id === user?.id) return 'Você'
                        return otherUser?.display_name || otherUser?.name || 'Usuário'
                      })
                      return (
                      <button
                        key={emoji}
                        className={`message-reaction ${users.some((u: MessageReaction) => u.user_id === user?.id) ? 'mine' : ''}`}
                        onClick={() => toggleReaction(m.id, emoji)}
                        title={names.join(', ')}
                      >
                        <span>{emoji}</span>
                        <span className="message-reaction-count">{users.length}</span>
                        <span className="message-reaction-names">{names.join(', ')}</span>
                      </button>
                      )
                    })}
                  </div>
                )}
              </div>
              </div>
            )
          })
          }
          </>
        )}
        {sending && (
          <div className="hermes-chat-msg user">
            <div className="hermes-chat-msg-avatar"><LoaderIcon size={18} /></div>
            <div className="hermes-chat-msg-body">
              <div className="hermes-chat-msg-content" style={{ color: 'var(--text-muted)' }}>Enviando...</div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      </>
      ) : (
      <div className="dm-media-panel">
        {mediaFiles.length === 0 ? (
          <div className="dm-media-empty">
            <ImageIcon size={48} />
            <p>Nenhuma mídia ou arquivo nesta conversa</p>
          </div>
        ) : (
          <>
            {imageFiles.length > 0 && (
              <div className="dm-media-section">
                <h4>Imagens ({imageFiles.length})</h4>
                <div className="dm-media-grid">
                  {imageFiles.map(m => (
                    <div
                      key={m.id}
                      className="dm-media-item dm-media-image"
                      onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                    >
                      <img src={attUrl(m)} alt={m.attachment_name || ''} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {videoFiles.length > 0 && (
              <div className="dm-media-section">
                <h4>Vídeos ({videoFiles.length})</h4>
                <div className="dm-media-grid">
                  {videoFiles.map(m => (
                    <div
                      key={m.id}
                      className="dm-media-item dm-media-video"
                      onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                    >
                      <video src={attUrl(m)} preload="metadata" />
                      <div className="dm-media-play-overlay"><VideoIcon size={24} /></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {audioFiles.length > 0 && (
              <div className="dm-media-section">
                <h4>Áudios ({audioFiles.length})</h4>
                <div className="dm-media-list">
                  {audioFiles.map(m => (
                    <div key={m.id} className="dm-media-audio-item">
                      <WhatsAppAudioPlayer
                        src={attUrl(m)}
                        fileName={m.attachment_name || 'audio'}
                        isOutgoing={m.sender_id === user?.id}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {docFiles.length > 0 && (
              <div className="dm-media-section">
                <h4>Arquivos ({docFiles.length})</h4>
                <div className="dm-media-list">
                  {docFiles.map(m => (
                    <button
                      key={m.id}
                      onClick={() => downloadAttachment(attUrl(m), m.attachment_name)}
                      className="dm-media-doc-item"
                    >
                      <FileIcon size={20} />
                      <span className="dm-media-doc-name">{m.attachment_name || 'Arquivo'}</span>
                      <DownloadIcon size={16} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {activeTab === 'chat' && (
      <div className="hermes-chat-input-area">
        <ChatInputBar
          onSend={handleSend}
          onSendAudio={handleSendAudio}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          replyLabel={`Respondendo a ${replyTo?.sender_id === user?.id ? 'você' : (otherUser?.display_name || otherUser?.name || 'usuário')}`}
          placeholder="Mensagem..."
          disabled={sending}
          mentionUsers={otherUser ? [otherUser] : []}
        />
      </div>
      )}

      {/* Message edit dropdown */}
      {msgOptionsMenu && (
        <div
          className="msg-options-dropdown"
          style={{
            position: 'fixed',
            left: Math.min(msgOptionsMenu.x, window.innerWidth - 160),
            top: msgOptionsMenu.y,
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {msgOptionsMenu.canCopy && (
            <button
              className="msg-options-item"
              onClick={async () => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m) {
                  const text = m.content || ''
                  await copyToClipboard(text)
                }
                setMsgOptionsMenu(null)
              }}
            >
              <CopyIcon size={16} /> Copiar
            </button>
          )}
          {msgOptionsMenu.canEdit && (
            <button
              className="msg-options-item"
              onClick={() => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m) startEditing(m)
                setMsgOptionsMenu(null)
              }}
            >
              <EditIcon size={16} /> Editar
            </button>
          )}
          {msgOptionsMenu.canDelete && (
            <button
              className="msg-options-item msg-options-item-danger"
              onClick={async () => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m && confirm('Apagar esta mensagem?')) {
                  try {
                    await fetch(`${API_URL}/api/dm/delete/${m.id}`, {
                      method: 'DELETE',
                      headers: authHeaders,
                    })
                    setMessages(prev => prev.filter(x => x.id !== m.id))
                  } catch (err: unknown) {
                    console.error('Failed to delete message:', err)
                  }
                }
                setMsgOptionsMenu(null)
              }}
            >
              <TrashIcon size={16} /> Apagar
            </button>
          )}
        </div>
      )}

      {/* Context menu (right-click on user name) */}
      {contextMenu && (
        <div
          className="dm-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="dm-context-item"
            onClick={() => { setShowProfileModal(true); setContextMenu(null) }}
          >
            <UserIcon size={16} /> Ver perfil
          </button>
          {mutedUsers?.has(userId ?? '') ? (
            <button
              className="dm-context-item"
              onClick={() => { unmuteUser?.(userId!); setContextMenu(null) }}
            >
              <BellIcon size={16} /> Reativar notificações
            </button>
          ) : (
            <button
              className="dm-context-item"
              onClick={() => { muteUser?.(userId!); setContextMenu(null) }}
            >
              <BellOffIcon size={16} /> Silenciar notificações
            </button>
          )}
        </div>
      )}

      {/* User profile modal */}
      {showProfileModal && otherUser && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content dm-profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Perfil do usuário</h3>
              <button className="icon-btn" onClick={() => setShowProfileModal(false)} title="Fechar">
                <XIcon size={18} />
              </button>
            </div>
            <div className="dm-profile-body">
              <div className="dm-profile-avatar">
                {otherUser.avatar_url ? (
                  <img src={avatarUrl(otherUser)} alt="" className="dm-profile-avatar-img" />
                ) : (
                  <div className="dm-profile-avatar-fallback"><UserIcon size={48} /></div>
                )}
              </div>
              <h2 className="dm-profile-name">{otherUser.display_name || otherUser.name || 'Sem nome'}</h2>
              <p className="dm-profile-email">{otherUser.email}</p>
              {otherUser.status_message && (
                <p className="dm-profile-status-message" style={{ color: 'var(--text-muted)', fontStyle: 'italic', margin: '4px 0 12px', fontSize: 13 }}>
                  "{otherUser.status_message}"
                </p>
              )}
              <div className="dm-profile-info">
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Nome</span>
                  <span className="dm-profile-info-value">{otherUser.name || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Display</span>
                  <span className="dm-profile-info-value">{otherUser.display_name || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Email</span>
                  <span className="dm-profile-info-value">{otherUser.email || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">ID</span>
                  <span className="dm-profile-info-value">#{otherUser.id}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Status</span>
                  <span className="dm-profile-info-value">
                    <PresenceDot status={otherUser.presence_status || 'offline'} size={8} />
                    {' '}{otherUser.presence_status || 'offline'}
                  </span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Função</span>
                  <span className="dm-profile-info-value">{otherUser.role || '-'}</span>
                </div>
                {otherUser.created_at && (
                  <div className="dm-profile-info-row">
                    <span className="dm-profile-info-label">Membro desde</span>
                    <span className="dm-profile-info-value">{new Date(otherUser.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                {otherUser.last_seen_at && (
                  <div className="dm-profile-info-row">
                    <span className="dm-profile-info-label">Último acesso</span>
                    <span className="dm-profile-info-value">{formatTime(otherUser.last_seen_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment preview modal */}
      {previewAttachment && (
        <div className="dm-preview-overlay" onClick={() => setPreviewAttachment(null)}>
          <div className="dm-preview-modal" onClick={e => e.stopPropagation()}>
            <button className="dm-preview-close" onClick={() => setPreviewAttachment(null)}>
              <XIcon size={20} />
            </button>
            {previewAttachment.type && previewAttachment.type.startsWith('image/') ? (
              <img src={previewAttachment.url} alt={previewAttachment.name} className="dm-preview-img" />
            ) : previewAttachment.type && previewAttachment.type.startsWith('video/') ? (
              <video src={previewAttachment.url} controls autoPlay className="dm-preview-video" />
            ) : (
              <div className="dm-preview-file">
                {attachmentIcon(previewAttachment.type)}
                <span className="dm-preview-file-name">{previewAttachment.name || 'Arquivo'}</span>
                <a href={previewAttachment.url} download={previewAttachment.name} className="btn-primary">
                  <DownloadIcon size={16} /> Baixar
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
